<template>
  <div class="font-sans min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
    <div class="flex-grow pb-12 lg:pb-20">
      <!-- Hero Section -->
      <section
        class="relative bg-blue-50 dark:bg-gray-900 pt-6 pb-8 lg:pt-10 lg:pb-12 overflow-hidden border-b border-gray-100 dark:border-gray-700"
      >
        <!-- Watermark Background -->
        <div
          class="absolute inset-0 opacity-[0.03] pointer-events-none overflow-hidden flex flex-wrap content-start"
        >
          <svg
            v-for="i in 150"
            :key="i"
            class="w-16 h-16 text-[#293146] dark:text-gray-700 m-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            ></path>
          </svg>
        </div>

        <div class="relative max-w-screen-xl mx-auto px-4 z-10">
          <router-link
            to="/"
            class="inline-flex items-center text-sm font-semibold text-[#293146] dark:text-gray-200 hover:text-[#C5B47A] dark:hover:text-[#C5B47A] mb-6 transition-colors group"
          >
            <svg
              class="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
            Kembali ke Beranda
          </router-link>

          <div
            class="bg-white dark:bg-gray-800 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none border border-gray-100 dark:border-gray-700 p-5 md:p-10 relative z-20"
          >
            <div class="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div
                class="w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-2xl flex items-center justify-center text-[#293146] dark:text-blue-300 shrink-0"
              >
                <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <div>
                <h1 class="text-2xl md:text-4xl font-black text-[#293146] dark:text-white mb-2">
                  FAQ
                </h1>
                <p class="text-sm md:text-base text-gray-500 dark:text-gray-300 font-medium">
                  Pertanyaan yang sering diajukan seputar layanan lokerjambi.co.id
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Content -->
      <main class="max-w-4xl mx-auto px-4 mt-10">
        <!-- Tags Filtering -->
        <div
          v-if="allTags.length > 0"
          class="flex flex-wrap items-center justify-center gap-2 mb-10"
        >
          <button
            @click="selectedTag = null"
            :class="
              !selectedTag
                ? 'bg-[#293146] dark:bg-[#C5B47A] text-white dark:text-gray-900 shadow-lg'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            "
            class="px-5 py-2 rounded-full text-xs font-black transition-all border border-gray-100 dark:border-gray-700"
          >
            Semua
          </button>
          <button
            v-for="tag in allTags"
            :key="tag"
            @click="selectedTag = tag"
            :class="
              selectedTag === tag
                ? 'bg-[#293146] dark:bg-[#C5B47A] text-white dark:text-gray-900 shadow-lg'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            "
            class="px-5 py-2 rounded-full text-xs font-black transition-all border border-gray-100 dark:border-gray-700"
          >
            {{ tag }}
          </button>
        </div>

        <div v-if="isLoading" class="space-y-6">
          <div
            v-for="i in 5"
            :key="i"
            class="bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm animate-pulse"
          >
            <div class="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
            <div class="h-4 bg-gray-100 dark:bg-gray-600 rounded w-full"></div>
          </div>
        </div>

        <div v-else-if="filteredFaqs.length > 0" class="space-y-4 md:space-y-6">
          <div
            v-for="faq in filteredFaqs"
            :key="faq.id"
            class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 md:border-2 rounded-[24px] md:rounded-[32px] overflow-hidden shadow-sm hover:shadow-lg dark:hover:shadow-xl dark:hover:shadow-gray-900/50 transition-all duration-300"
          >
            <button
              @click="toggleFaq(faq.id)"
              class="w-full flex items-center justify-between p-5 md:p-8 text-left hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors group"
            >
              <div class="flex-1 min-w-0">
                <div v-if="faq.tags && faq.tags.length > 0" class="flex flex-wrap gap-1.5 mb-3">
                  <span
                    v-for="tag in faq.tags"
                    :key="tag"
                    class="text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-300 bg-blue-50/50 dark:bg-blue-900/50 px-2 py-0.5 rounded-md"
                  >
                    {{ tag }}
                  </span>
                </div>
                <span
                  class="text-base md:text-xl font-bold text-[#293146] dark:text-white leading-tight group-hover:text-blue-700 dark:group-hover:text-[#C5B47A] transition-colors"
                  >{{ faq.question }}</span
                >
              </div>
              <div
                class="shrink-0 ml-4 w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-gray-600 transition-colors"
              >
                <svg
                  class="w-5 h-5 md:w-6 md:h-6 text-gray-400 dark:text-gray-500 transition-transform duration-300"
                  :class="{
                    'rotate-180 text-blue-600 dark:text-[#C5B47A]': activeFaq === faq.id,
                  }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>
            </button>
            <Transition
              enter-active-class="transition duration-300 ease-out"
              enter-from-class="opacity-0 -translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-200 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 -translate-y-2"
            >
              <div
                v-show="activeFaq === faq.id"
                class="px-5 pb-6 md:px-8 md:pb-8 text-gray-600 dark:text-gray-300 leading-relaxed"
              >
                <div
                  class="prose prose-sm md:prose-base max-w-none prose-blue text-gray-600 dark:text-gray-300"
                  v-sanitize="faq.answer"
                ></div>
              </div>
            </Transition>
          </div>
        </div>

        <div
          v-else
          class="bg-white dark:bg-gray-800 rounded-[32px] p-12 text-center border-2 border-dashed border-gray-200 dark:border-gray-700"
        >
          <div
            class="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <svg
              class="w-10 h-10 text-gray-300 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <p class="text-gray-500 dark:text-gray-400 font-medium">
            Tidak ada pertanyaan yang sesuai dengan filter saat ini.
          </p>
          <button
            v-if="selectedTag"
            @click="selectedTag = null"
            class="mt-4 text-blue-600 dark:text-[#C5B47A] font-bold hover:underline dark:hover:text-[#C5B47A]"
          >
            Reset Filter
          </button>
        </div>

        <div class="mt-12 text-center">
          <p class="text-gray-500 dark:text-gray-400 mb-6 font-medium">
            Masih punya pertanyaan lainnya?
          </p>
          <router-link
            to="/contact"
            class="inline-flex items-center px-8 py-3 bg-[#293146] dark:bg-[#C5B47A] text-white dark:text-gray-900 font-bold rounded-xl hover:bg-[#3b4660] dark:hover:bg-yellow-500 transition-all shadow-lg hover:shadow-[#293146]/20 dark:hover:shadow-[#C5B47A]/20"
          >
            Hubungi Kami
            <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              ></path>
            </svg>
          </router-link>
        </div>
      </main>
    </div>
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { initFlowbite } from 'flowbite';
import AppFooter from '../components/layout/AppFooter.vue';
import { useFaq } from '../composables/useFaq';
import { useSeoMeta } from '../composables/useSeoMeta';

useSeoMeta({
  title: 'FAQ — Pertanyaan yang Sering Diajukan',
  description:
    'Temukan jawaban atas pertanyaan umum seputar cara melamar kerja, keamanan data, fitur Lokerjambi.co.id, dan layanan lainnya. Panduan lengkap untuk pencari kerja di Jambi.',
  path: '/faq',
});

const { faqs, isLoading, activeFaq, allTags, fetchFaqs, toggleFaq } = useFaq();
const selectedTag = ref(null);

const filteredFaqs = computed(() => {
  if (!selectedTag.value) return faqs.value;
  return faqs.value.filter((faq) => faq.tags && faq.tags.includes(selectedTag.value));
});

onMounted(() => {
  initFlowbite();
  fetchFaqs();
});
</script>

<style scoped>
.prose {
  color: inherit;
}
</style>
